import React, { useState } from 'react';
import { Upload, FileText, AlertTriangle, CheckCircle, BarChart2, Activity, ShieldAlert } from 'lucide-react';

export const ReturnsTab = () => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
            setError(null);
            setReport(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setError("Please select a file first.");
            return;
        }

        setIsUploading(true);
        setError(null);
        setReport(null); // Clear previous report when starting a new upload

        const formData = new FormData();
        formData.append('file', file);

        try {
            // 1. Start Job
            const token = localStorage.getItem('token');
            const res = await fetch('/api/returns/import', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!res.ok) {
                let errMsg = "Upload failed";
                try {
                    const errData = await res.json();
                    errMsg = errData.error || errMsg;
                } catch {
                    errMsg = await res.text();
                }
                throw new Error(errMsg);
            }

            const data = await res.json();
            const jobId = data.jobId;

            // 2. Poll Status
            const pollInterval = setInterval(async () => {
                try {
                    const statusRes = await fetch(`/api/returns/status/${jobId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (statusRes.ok) {
                        const job = await statusRes.json();

                        if (job.status === 'completed') {
                            clearInterval(pollInterval);
                            setReport({ summary: job.summary });
                            setIsUploading(false);
                            setFile(null); // Clear the selected file after successful upload and analysis
                            // Optionally, reset the file input element if needed, though setFile(null) handles the display
                        } else if (job.status === 'failed') {
                            clearInterval(pollInterval);
                            setError("Analysis Failed: " + (job.error || "Unknown error"));
                            setIsUploading(false);
                        }
                        // else: still processing, keep waiting
                    } else {
                        // Handle non-OK status for polling request
                        clearInterval(pollInterval);
                        let pollErrMsg = "Failed to get job status";
                        try {
                            const errData = await statusRes.json();
                            pollErrMsg = errData.error || pollErrMsg;
                        } catch {
                            pollErrMsg = await statusRes.text();
                        }
                        setError(pollErrMsg);
                        setIsUploading(false);
                    }
                } catch (err) {
                    console.error("Polling Error:", err);
                    clearInterval(pollInterval);
                    setError("Polling error: " + err.message);
                    setIsUploading(false);
                }
            }, 2000); // Check every 2s

        } catch (err) {
            console.error("Upload error:", err);
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="h-full flex flex-col gap-6 text-[var(--color-text-primary)] animate-in fade-in duration-500">

            {/* Header Section */}
            <div className="flex items-center justify-between border-b border-white/5 pb-4">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Activity className="text-blue-500" /> Returns & Error Analysis
                    </h2>
                    <p className="text-stone-400 text-sm mt-1">
                        Import service reports, classify errors using AI, and generate management summaries.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6 h-full overflow-hidden">

                {/* Left Panel: Upload & Stats */}
                <div className="col-span-4 flex flex-col gap-6 overflow-y-auto pr-2">

                    {/* Upload Card */}
                    <div className="bg-black/20 border border-white/5 rounded-xl p-6 shadow-lg">
                        <h3 className="font-semibold mb-4 flex items-center gap-2"><Upload size={18} /> New Import</h3>

                        <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${file ? 'border-green-500/50 bg-green-500/5' : 'border-stone-700 hover:border-stone-500 hover:bg-stone-800'}`}>
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                                className="hidden"
                                id="file-upload"
                            />
                            <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-3">
                                {file ? (
                                    <FileText size={40} className="text-green-500" />
                                ) : (
                                    <FileText size={40} className="text-stone-500" />
                                )}
                                <span className="text-sm font-medium text-stone-300">
                                    {file ? file.name : "Click to Select Excel File"}
                                </span>
                                {!file && <span className="text-xs text-stone-500">.xlsx files supported</span>}
                            </label>
                        </div>

                        <button
                            onClick={handleUpload}
                            disabled={!file || isUploading}
                            className={`w-full mt-4 py-2.5 rounded-lg font-medium transition-all ${!file || isUploading
                                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/20'
                                }`}
                        >
                            {isUploading ? "Analyzing..." : "Start Analysis"}
                        </button>

                        {error && (
                            <div className="mt-4 p-3 bg-red-900/20 border border-red-800 rounded text-red-300 text-sm flex items-start gap-2">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Stats Card (Only if report exists) */}
                    {report && report.summary && (
                        <div className="bg-black/20 border border-white/5 rounded-xl p-6 shadow-lg animate-in slide-in-from-left-4 duration-500">
                            <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart2 size={18} /> Import Statistics</h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-stone-800/50 p-3 rounded border border-stone-700">
                                    <div className="text-xs text-stone-400 uppercase">Total Records</div>
                                    <div className="text-xl font-bold text-white">{report.summary.total}</div>
                                </div>
                                <div className="bg-green-900/20 p-3 rounded border border-green-800/30">
                                    <div className="text-xs text-green-400 uppercase">Imported</div>
                                    <div className="text-xl font-bold text-green-400">{report.summary.imported}</div>
                                </div>
                                <div className="bg-stone-800/50 p-3 rounded border border-stone-700">
                                    <div className="text-xs text-stone-400 uppercase">Duplicates</div>
                                    <div className="text-xl font-bold text-stone-300">{report.summary.duplicates}</div>
                                </div>
                                <div className="bg-red-900/20 p-3 rounded border border-red-800/30">
                                    <div className="text-xs text-red-400 uppercase">Errors</div>
                                    <div className="text-xl font-bold text-red-400">{report.summary.errors}</div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {/* Right Panel: AI Report */}
                <div className="col-span-8 bg-black/30 border border-stone-700 rounded-xl overflow-hidden flex flex-col shadow-xl">
                    <div className="bg-black/20 border-b border-stone-700 px-6 py-4 flex items-center justify-between">
                        <h3 className="font-semibold flex items-center gap-2 text-purple-300">
                            <ShieldAlert size={18} /> AI Management Summary
                        </h3>
                        {report && <span className="text-xs px-2 py-1 bg-purple-900/30 text-purple-300 rounded border border-purple-800">Generated by Mistral-Large</span>}
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 bg-black/50">
                        {!report ? (
                            <div className="h-full flex flex-col items-center justify-center text-stone-600">
                                {isUploading ? (
                                    <div className="flex flex-col items-center animate-in fade-in duration-500">
                                        <Activity size={64} className="mb-6 text-blue-500 animate-pulse" />
                                        <h3 className="text-xl font-semibold text-stone-300 mb-2">Analyzing Data...</h3>
                                        <p className="text-stone-500 max-w-sm text-center">
                                            Classifying errors and generating management summary. This may take a minute.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <BarChart2 size={48} className="mb-4 opacity-20" />
                                        <p>Upload a file to generate the analysis report.</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="prose prose-invert prose-stone max-w-none whitespace-pre-wrap font-sans text-stone-300">
                                {report.summary.ai_report || "No analysis generated."}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};
